import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { __, sprintf } from '@wordpress/i18n';
import useFetch from '../../Hooks/useFetch';

import './style.scss';

/**
 * OptInForm component.
 *
 * @param {Object} data		- Plugin data from PHP.
 * @param {boolean} popup	- When true, renders as a modal overlay triggered by the
 * custom "fsLiteOpenOptIn" event instead of inline.
 */
const OptInForm = ({ data, popup = false }) => {
	const [isExtensionsAllowed, setIsExtensionsAllowed] = useState(true);
	const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
	const [isVisible, setIsVisible] = useState(!popup); // hidden by default in popup mode

	const { id, slug, name, icons, user, site, technical, urls, nonces, standalone, public_key, version, sdk_version, ajax_url } = data;

	const { request, isLoading, error } = useFetch(ajax_url);

	// In standalone (page) mode, add body class
	useEffect(() => {
		if (standalone && !popup) {
			document.body.classList.add('fs-standalone');
		}
	}, [standalone, popup]);

	// In popup mode, listen for the open event dispatched by the Opt In action link
	useEffect(() => {
		if (!popup) return;

		const handleOpen = () => setIsVisible(true);
		document.addEventListener(`fsLiteOpenOptIn_${id}`, handleOpen);
		return () => document.removeEventListener(`fsLiteOpenOptIn_${id}`, handleOpen);
	}, [popup, id]);

	// Trap body scroll while popup is open
	useEffect(() => {
		if (popup) {
			document.body.style.overflow = isVisible ? 'hidden' : '';
		}
		return () => { if (popup) document.body.style.overflow = ''; };
	}, [popup, isVisible]);

	const handleConnect = async (e) => {
		e.preventDefault();

		const params = {
			sdk_version,
			platform_version: technical.wp_version,
			programming_language_version: technical.php_version,
			plugin_slug: slug,
			plugin_id: id,
			plugin_public_key: public_key,
			plugin_version: version,
			return_url: urls.return,
			account_url: urls.account,
			is_premium: false, // Force false for Lite SDK
			is_active: true,
			is_localhost: technical.is_localhost,
			user_firstname: user.first_name,
			user_lastname: user.last_name,
			user_email: user.email,
			title: site.name,
			language: technical.language,
			uid: site.uid,
			url: site.url,
			is_extensions_tracking_allowed: isExtensionsAllowed,
			is_diagnostic_tracking_allowed: true,
		};

		const result = await request(`fs_connect_${id}`, nonces.connect, params);

		if (result.success) {
			window.location.reload();
		}
	};

	const handleSkip = async (e) => {
		e.preventDefault();

		const result = await request(`fs_dismiss_${id}`, nonces.dismiss);

		if (result.success) {
			if (popup) {
				setIsVisible(false);
				window.location.reload();
			} else if (standalone) {
				const url = new URL(window.location.href);
				url.searchParams.delete('fs_connect');
				window.location.href = url.toString();
			} else {
				window.location.reload();
			}
		}
	};

	const handleClose = (e) => {
		e.preventDefault();
		setIsVisible(false);
	};

	// ── Popup (modal overlay) mode ────────────────────────────────────────
	if (popup) {
		if (!isVisible) return null;

		return createPortal(
			<div className="pevFSLite pev-fs-modal fs-modal-opt-in" style={{ display: 'flex' }}>
				<div className="pev-fs-modal-dialog">
					<div className="fs-modal-header">
						<h4>{__('Allow & Continue', 'pev-fs-lite')}</h4>
						<a href="#!" className="fs-close" onClick={handleClose}>
							<i className="dashicons dashicons-no" title="Dismiss"></i>
						</a>
					</div>
					<div className="fs-modal-body">
						{error && <div className="fs-error">{error}</div>}
						<div className="fs-plugin-icon" style={{ textAlign: 'center', marginBottom: '12px' }}>
							<img
								src={icons['2x']}
								width="64"
								height="64"
								alt={name}
								onError={(e) => { e.target.src = icons['1x']; }}
								style={{ borderRadius: '50%' }}
							/>
						</div>
						<p>{__('Never miss an important update', 'pev-fs-lite')}</p>
						<p style={{ fontSize: '13px', color: '#555' }}>
							{__('Opt in to get email notifications for security & feature updates, educational content, and occasional offers, and to share some basic WordPress environment info.', 'pev-fs-lite')}
						</p>
						<div className={`fs-permissions ${isPermissionsOpen ? 'fs-open' : ''}`}>
							<div
								className="fs-trigger"
								onClick={() => setIsPermissionsOpen(!isPermissionsOpen)}
							>
								{sprintf(__('This will allow %s to', 'pev-fs-lite'), name)}
								<span className="fs-arrow"></span>
							</div>
							<ul>
								<li>
									<i className="dashicons dashicons-admin-users"></i>
									<div className="fs-permission-description">
										<span>{__('View Basic Profile Info', 'pev-fs-lite')}</span>
										<p>{__("Your WordPress user's: first & last name, and email address", 'pev-fs-lite')}</p>
									</div>
								</li>
								<li>
									<i className="dashicons dashicons-admin-links"></i>
									<div className="fs-permission-description">
										<span>{__('View Basic Website Info', 'pev-fs-lite')}</span>
										<p>{__('Homepage URL & title, WP & PHP versions, and site language', 'pev-fs-lite')}</p>
									</div>
								</li>
								<li>
									<i className="dashicons dashicons-admin-plugins"></i>
									<div className="fs-permission-description">
										<span>{__('View Basic Plugin Info', 'pev-fs-lite')}</span>
										<p>{__('Current plugin & SDK versions, and if active or uninstalled', 'pev-fs-lite')}</p>
									</div>
								</li>
							</ul>
						</div>
					</div>
					<div className="fs-modal-footer">
						<button
							onClick={handleConnect}
							className={`button button-primary fs-connect ${isLoading ? 'fs-loading' : ''}`}
							disabled={isLoading}
						>
							{isLoading ? __('Connecting...', 'pev-fs-lite') : __('Allow & Continue', 'pev-fs-lite')}
						</button>
						<button
							onClick={handleSkip}
							className="button button-secondary fs-dismiss"
							disabled={isLoading}
						>
							{__('Skip', 'pev-fs-lite')}
						</button>
					</div>
				</div>
			</div>,
			document.body
		);
	}

	// ── Inline / standalone mode ──────────────────────────────────────────
	return (
		<div className={`fs-connect wrap ${standalone ? 'fs-standalone-view' : ''}`}>
			<div className="fs-header">
				<div className="fs-plugin-icon">
					<img
						src={icons['2x']}
						width="80"
						height="80"
						alt={name}
						onError={(e) => { e.target.src = icons['1x']; }}
					/>
				</div>
			</div>
			<div className="fs-box-container">
				<div className="fs-content">
					{error && <div className="fs-error">{error}</div>}
					<h2>{__('Never miss an important update')}</h2>
					<p>{__('Opt in to get email notifications for security & feature updates, educational content, and occasional offers, and to share some basic WordPress environment info. This will help us make the plugin more compatible with your site and better at doing what you need it to.')}</p>
				</div>
				<div className="fs-actions">
					<button
						onClick={handleConnect}
						className={`button button-primary fs-connect ${isLoading ? 'fs-loading' : ''}`}
						disabled={isLoading}
					>
						{isLoading ? __('Connecting...') : __('Allow & Continue')}
					</button>
					<button
						onClick={handleSkip}
						className="button button-secondary fs-dismiss"
						disabled={isLoading}
					>
						{__('Skip')}
					</button>
				</div>
				<div className={`fs-permissions ${isPermissionsOpen ? 'fs-open' : ''}`}>
					<div
						className="fs-trigger"
						onClick={() => setIsPermissionsOpen(!isPermissionsOpen)}
					>
						{sprintf(__('This will allow %s to'), name)}
						<span className="fs-arrow"></span>
					</div>
					<ul>
						<li>
							<i className="dashicons dashicons-admin-users"></i>
							<div className="fs-permission-description">
								<span className="fs-tooltip-trigger">
									{__('View Basic Profile Info')}
									<i className="dashicons dashicons-editor-help">
										<span className="fs-tooltip">
											{__('Never miss important updates, get security warnings before they become public knowledge, and receive notifications about special offers and awesome new features.')}
										</span>
									</i>
								</span>
								<p>{__("Your WordPress user's: first & last name, and email address")}</p>
							</div>
						</li>
						<li>
							<i className="dashicons dashicons-admin-links"></i>
							<div className="fs-permission-description">
								<span className="fs-tooltip-trigger">
									{__('View Basic Website Info')}
									<i className="dashicons dashicons-editor-help">
										<span className="fs-tooltip">
											{__("To provide additional functionality that's relevant to your website, avoid WordPress or PHP version incompatibilities that can break your website, and recognize which languages & regions the plugin should be translated and tailored to.")}
										</span>
									</i>
								</span>
								<p>{__('Homepage URL & title, WP & PHP versions, and site language')}</p>
							</div>
						</li>
						<li>
							<i className="dashicons dashicons-admin-plugins"></i>
							<div className="fs-permission-description">
								<span>{__('View Basic Plugin Info')}</span>
								<p>{__('Current plugin & SDK versions, and if active or uninstalled')}</p>
							</div>
						</li>
						<li>
							<i className="dashicons dashicons-block-default"></i>
							<div className="fs-permission-description">
								<span className="fs-tooltip-trigger">
									{__('View Plugins & Themes List')}
									<i className="dashicons dashicons-editor-help">
										<span className="fs-tooltip">
											{__('To ensure compatibility and avoid conflicts with your installed plugins and themes.')}
										</span>
									</i>
								</span>
								<p>{__('Names, slugs, versions, and if active or not')}</p>
							</div>
							<div
								className={`fs-switch ${isExtensionsAllowed ? 'fs-on' : ''}`}
								onClick={() => setIsExtensionsAllowed(!isExtensionsAllowed)}
							>
								<div className="fs-toggle"></div>
							</div>
						</li>
					</ul>
				</div>
			</div>
			<div className="fs-terms">
				<a href={`https://freemius.com/product/opt-in/${id}/${slug}/`} target="_blank" rel="noopener noreferrer">
					{__('Powered by Freemius')}
				</a>
				{' - '}
				<a href="https://freemius.com/privacy/" target="_blank" rel="noopener noreferrer">
					{__('Privacy Policy')}
				</a>
				{' - '}
				<a href={`https://freemius.com/product/opt-in/${id}/${slug}/`} target="_blank" rel="noopener noreferrer">
					{__('Terms of Service')}
				</a>
			</div>
		</div>
	);
};

export default OptInForm;
